'use client';

import useLoginModal from '@fe-news/hooks/use-login-modal';
import useMyCollection from '@fe-news/hooks/use-my-collection';
import useUpdateMyCollection from '@fe-news/hooks/use-update-my-collection';
import { styled } from '@linaria/react';
import { useEffect, useState } from 'react';
import LoginModal from '@fe-news/components/molecules/LoginModal';
import Modal from '@fe-news/components/molecules/Modal';
import { SCREEN_SIZE } from '@fe-common-utils/constants/screen';
import { GaActionType } from '@fe-cnyes/fe-common-gtm';
import SuccessFollowPopUp from '@fe-news/components/molecules/SuccessFollowPopUp';
import Image from 'next/image';

// types
import { MyCollectionProductType, LIMIT_POP_TOPIC_COUNT } from '@fe-news/constants/my-track/type';

// utils
import { isBookmark, handleFollow } from '@fe-news/utils/member';

const Button = styled.button<{ isFollow?: boolean; isDetail: boolean; isAside: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ isFollow, isDetail, isAside }) =>
    isFollow ? (isDetail ? '#515c69' : isAside ? '#e03f19' : '#fff') : '#fff'};
  font-size: 16px;
  padding: 5px 8px;
  border: ${({ isFollow, isDetail, isAside }) =>
    isFollow
      ? isDetail
        ? 'solid 1px #515c69'
        : isAside
          ? '1px solid #e03f19'
          : '1px solid #fff'
      : '1px solid #e03f19'};
  background-color: ${({ isFollow }) => (isFollow ? 'transparent' : '#e03f19')};
  border-radius: 6px;
  cursor: pointer;
  position: relative;

  &::after {
    content: '';
    background: ${({ isFollow, isDetail, isAside }) =>
      isFollow
        ? isDetail
          ? "url('/assets/icons/icon-add-g.svg')"
          : isAside
            ? "url('/assets/icons/icon-add-r.svg')"
            : "url('/assets/icons/icon-add-w.svg')"
        : "url('/assets/icons/icon-add-check.svg')"};
    width: 16px;
    height: 16px;
  }

  &:disabled {
    cursor: not-allowed;
  }

  img {
    position: absolute;
    right: -16px;
    top: -20px;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 398px;

  @media screen and (max-width: ${SCREEN_SIZE.TABLET}px) {
    min-width: 327px;
  }

  h3 {
    font-size: 18px;
    font-weight: 500;
    letter-spacing: 0.5px;
    text-align: center;
  }

  p {
    margin-top: 16px;
    font-size: 16px;
    letter-spacing: 0.5px;
    color: #383838;
    text-align: center;
  }

  button {
    margin-top: 24px;
    font-size: 14px;
    background-color: #e03f19;
    color: #fff;
    padding: 9px 14px;
    border-radius: 4px;
    cursor: pointer;
    align-self: center;
  }
`;

type FollowButtonProps = {
  isDetail?: boolean;
  isAside?: boolean;
  id: number;
  gtmEvent?: GaActionType;
  style?: React.CSSProperties;
  isRankingList?: boolean;
  isNeedAutoUnFollow?: boolean;
  isShowNewTip?: boolean;
};

const FollowButton: React.FC<FollowButtonProps> = ({
  isDetail = false,
  isAside = false,
  id,
  gtmEvent,
  style,
  isRankingList = false,
  isNeedAutoUnFollow = false,
  isShowNewTip = false
}) => {
  const [isClose, setIsClose] = useState(true);
  const [isSaved, setIsSaved] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { isLoggedIn, isCloseLoginModal, onClickLoginModalOpen, onClickLoginModalClose } = useLoginModal({
    defaultCloseLoginModal: true
  });

  const { myCollectionDataItemsIdArray, refetch, isLoading } = useMyCollection({
    productType: MyCollectionProductType.POPTOPIC,
    isLogin: isLoggedIn,
    isRankingList
  });

  const {
    deleteMyCollection,
    postMyCollection,
    isLoading: isUpdateMyCollectionLoading
  } = useUpdateMyCollection({
    refetchMyCollection: refetch
  });

  const isBookmarked = isBookmark(myCollectionDataItemsIdArray, id.toString());

  const onClose = () => {
    setIsClose(true);
  };

  const handleDelete = async () => {
    const response = await deleteMyCollection({
      productType: MyCollectionProductType.POPTOPIC,
      data: [id.toString()]
    });
    return response.message ?? '';
  };

  const handlePost = async () => {
    const response = await postMyCollection({
      productType: MyCollectionProductType.POPTOPIC,
      data: [id.toString()]
    });
    return response.message ?? '';
  };

  const onClickFollow = () => {
    handleFollow({
      isLoggedIn,
      isBookmarked,
      newsId: id.toString(),
      dataLength: myCollectionDataItemsIdArray?.length,
      limit: LIMIT_POP_TOPIC_COUNT,
      productType: MyCollectionProductType.POPTOPIC,
      setIsSaved,
      setIsClose,
      setIsSuccess,
      handleDelete,
      handlePost,
      onClickLoginModalOpen,
      gtmEvent
    });
  };

  useEffect(() => {
    setIsSaved(isBookmarked);
  }, [isBookmarked]);

  useEffect(() => {
    if (isNeedAutoUnFollow) {
      handleDelete();
    }
  }, [isNeedAutoUnFollow]);

  return (
    <>
      {isSuccess && <SuccessFollowPopUp setIsSuccess={setIsSuccess} />}
      <Modal isClose={isClose} onClose={onClose}>
        <Content>
          <h3>提醒您</h3>
          <p>熱門時事追蹤數量已達上限 10 則</p>
          <button onClick={onClose}>返回</button>
        </Content>
      </Modal>
      <LoginModal isModalClosed={isCloseLoginModal} onClickModalClose={onClickLoginModalClose} />
      <Button
        isFollow={isSaved}
        isDetail={isDetail}
        isAside={isAside}
        disabled={isUpdateMyCollectionLoading || isLoading}
        onClick={onClickFollow}
        aria-label={isSaved ? 'un-follow' : 'follow'}
        style={style}
      >
        {isSaved ? '已追蹤' : '追蹤'}
        {isShowNewTip && (
          <Image className='new-tip' src='/assets/icons/icon-new-tip.png' alt='new tip' width={32} height={19} />
        )}
      </Button>
    </>
  );
};

export default FollowButton;
